/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";

import dynamic from "next/dynamic";
import Head from 'next/head';
import router, { useRouter } from "next/router";
import { useSelector } from "react-redux";

import AboutUsComponent from "../../components/BandComponent/AboutUsComponent/AboutUsComponent";
import BandConfiguration from "../../components/BandComponent/BandConfiguration/BandConfiguration";
import ExtraSongsComponent from "../../components/BandComponent/ExtraSongsComponent/ExtraSongsComponent";
import HeaderSliderComponent from "../../components/BandComponent/HeaderSliderComponent/HeaderSliderComponent";
import ReviewComponent from "../../components/BandComponent/ReviewComponent/ReviewComponent";
import TeaserSongListComponent from "../../components/BandComponent/TeaserSongListComponent/TeaserSongListComponent";
import FooterNavComponent from "../../components/FooterComponent/FooterNavComponent/FooterNavComponent";
import GetInTouchComponent from "../../components/FooterComponent/GetInTouchComponent/GetInTouchComponent";
import HeaderComponent from "../../components/HeaderComponent/HeaderComponent";
import { NextHead } from "../../components/NextHead/NextHead";
import { _fetchCmsPageData, _fetchFooterCmsData } from "../../helper/service";
import { AxiosInstance, logout } from "../../utilities";

// Avoid SSR width mismatch by dynamic import with ssr:false
const MobileFlow = dynamic(() => import("../../components/BandComponent/BandConfiguration/MobileFlow"), { ssr: false });
const BandDetailPage = (props) => {
  const { query, isReady } = useRouter();
  const bandState = useSelector((state) => state.band);

  const [bandDetails, setBandDetails] = useState([]);
  const [activeSection, setActiveSection] = useState("Band Videos");
  const [isSticky, setSticky] = useState(false);
  const [bandTotal, setBandTotal] = useState(0);
  const [totalPieces, setTotalPieces] = useState(0);
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [pageDetails, setPageDetails] = useState({});
  const [showBandVideos, setShowBandVideos] = useState(true);
  const [cmsData, setCmsData] = useState();
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const teaserSongsRef = useRef(null);
  const aboutUsRef = useRef(null);
  const bandConfigRef = useRef(null);
  const reviewRef = useRef(null);
  const navRef = useRef(null);

  const onBandTotalChange = (value) => {
    setBandTotal(value);
  };

  const onBandPiecesChange = (val) => {
    setTotalPieces(val);
  };

  const navList = [
    { name: "Band Videos", ref: teaserSongsRef },
    { name: "About Us", ref: aboutUsRef },
    { name: "Configure this Band", ref: bandConfigRef },
    { name: "Reviews", ref: reviewRef },
  ];

  useEffect(() => {
    if (!isReady) return;
    fetchBandDetails();
    fetchPageData();
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", () => handleScroll);
    };
  }, [props, router]);

  useEffect(() => {
    // Check if window is defined (client-side)
    if (typeof window !== 'undefined') {
      // Update isMobile state based on window width
      const checkIfMobile = () => setIsMobile(window.innerWidth < 768);
      
      // Initial check
      checkIfMobile();
      
      // Add event listener for window resize
      window.addEventListener('resize', checkIfMobile);
      
      // Cleanup
      return () => window.removeEventListener('resize', checkIfMobile);
    }
  }, []);

  const handleScroll = () => {
    navList.forEach(({ name, ref }) => {
      if (
        ref.current !== null &&
        ref.current.getBoundingClientRect().top <= 150
      ) {
        setActiveSection(name);
      }
    });
    if (navRef.current) {
      setSticky(navRef.current.getBoundingClientRect().top <= 120);
    }
  };

  const executeScroll = (name, sectionRef) => {
    setActiveSection(name);
    if (sectionRef.current !== null)
      window.scrollTo(0, sectionRef.current.offsetTop - 120);
  };

  const fetchPageData = () => {
    AxiosInstance(4008, "application/json")
      .post("/pages", {
        slug: "band-detail",
      })
      .then((resp) => {
        const response = resp?.data;
        if (response?.status === 200) {
          const cmsData = response.data.data;
          let temp1 = {};
          let temp2 = {};
          cmsData.map((item) => (temp1[item.key] = item.value));
          setPageDetails(temp1);
          cmsData.map((item) => (temp2[item.key] = item.description));
          setCmsData(temp2);
        } else if (response?.status === 400) {
          setPageDetails([]);
        }
      })
      .catch((err) => {
        var error = err && err.response;
        if (error && (error.status === "403" || error.status === 403)) {
          logout();
        } else if (error && (error.status === "500" || error.status === 500)) {
          console.log("Error", error.msg);
        }
      });
  };

  const fetchBandDetails = () => {
    const isShareLink = query.slug[0] === "share";
    AxiosInstance(4009, "application/json")
      .post("/details", {
        slug: isShareLink ? query.slug[1] : query.slug[0],
      })
      .then((resp) => {
        const response = resp && resp.data;
        if (
          response &&
          (response.status === 200 || response.status === "200")
        ) {
          setBandDetails(response.data);
          if (response.data.videos?.length < 1) {
            setShowBandVideos(false);
          }
        } else if (
          response &&
          (response.status === 400 || response.status === "400")
        ) {
          setBandDetails([]);
        }
      })
      .catch((err) => {
        var error = err && err.response;
        if (error && (error.status === "403" || error.status === 403)) {
          logout();
        } else if (error && (error.status === "500" || error.status === 500)) {
          console.log("Error", error.msg);
        }
      });
  };

  const handleMissingTravelData = () => {
    setShowSearchModal(false);
    //Set Modal Changed to false because its showing in desktop view
  };

  return (
    <React.Fragment>
      <NextHead {...props?.seo} />
      <Head>
        <link rel="canonical" href={`${process.env.BASE_URL}/band/${query.slug[0]}`} />
      </Head>
      <HeaderComponent
        showLoginModal={openLoginModal}
        onModalClose={() => setOpenLoginModal(false)}
        showSearchModal={showSearchModal}
      />
      <div>
        <HeaderSliderComponent
          data={bandDetails}
          bandCost={bandTotal}
          navRef={navRef}
          totalPieces={totalPieces}
        />
      </div>
      <div
        id="band_nav_bar"
        className={`band_detail_nav ${
          isSticky ? " band_detail_nav_sticky" : ""
        }`}
        ref={navRef}
      >
        <div className="d-flex align-items-center sticky-inner scrollbar-hidden">
          {navList
            .filter(
              (item) =>
                item.name !== "Reviews" || bandDetails?.numberOfReviews > 0
            )
            .map(({ name, ref }) => (
              // eslint-disable-next-line react/jsx-key
              <div
                key={name}
                className={`band_detail_nav_item ${
                  name === "Band Videos" && !showBandVideos
                    ? "hide-element"
                    : ""
                } ${activeSection === name ? "active" : ""}`}
                onClick={() => executeScroll(name, ref)}
              >
                <p className="m-0">{name}</p>
                <div className="active_nav_item"></div>
              </div>
            ))}
        </div>
      </div>
      {bandDetails?.videos?.length > 0 && (
        <div ref={teaserSongsRef}>
          <TeaserSongListComponent
            data={bandDetails.videos}
            cmsData={pageDetails}
            cmsDataCopy={cmsData}
          />
        </div>
      )}
      {bandDetails?.extraVideos?.length > 0 && (
        <ExtraSongsComponent
          data={bandDetails.extraVideos}
          cmsData={pageDetails}
        />
      )}
      {bandDetails?.aboutUs && (
        <div ref={aboutUsRef}>
          <AboutUsComponent data={bandDetails.aboutUs} />
        </div>
      )}
      <div ref={bandConfigRef} id="configure-this-band">
        {isMobile ? (
          <MobileFlow
            slug={query.slug[0]}
            bandId={bandDetails._id}
            bandTotal={bandTotal}
            openLoginModal={openLoginModal}
            onBandTotalChange={onBandTotalChange}
            onBandPiecesChange={onBandPiecesChange}
            onSaveClick={(value) => setOpenLoginModal(value)}
            cmsData={cmsData}
            bandStateData={bandState}
            onMissingTravelData={handleMissingTravelData}
          />
        ) : (
          <BandConfiguration
            slug={query.slug[0]}
            bandId={bandDetails._id}
            openLoginModal={openLoginModal}
            onBandTotalChange={onBandTotalChange}
            onBandPiecesChange={onBandPiecesChange}
            onSaveClick={(value) => setOpenLoginModal(value)}
            cmsData={cmsData}
            bandStateData={bandState}
            onMissingTravelData={handleMissingTravelData}
          />
        )}
      </div>
      {bandDetails?.numberOfReviews > 0 && (
        <div ref={reviewRef}>
          <ReviewComponent bandId={bandDetails._id} cmsData={pageDetails} />
        </div>
      )}
      <GetInTouchComponent />
      <FooterNavComponent />
    </React.Fragment>
  );
};

export default BandDetailPage;

export async function getServerSideProps(context) {
  // let cmsData = {}
  let seo = {};
  let notFound = false;

  //CmsData
  // const cmsResponse = await _fetchCmsPageData({ slug: "band-detail"})
  //   cmsData = cmsResponse?.data || {}
  //   seo = cmsResponse?.seo || {}
  await AxiosInstance(4009, "application/json")
    .post("/details", {
      slug: context.query.slug[0],
    })
    .then((resp) => {
      const response = resp?.data;
      if (response?.status === 200) {
        seo = response?.data?.seo || {};
      }
      else if(response?.status === 404) notFound = true 
    });

  const footerData = await _fetchFooterCmsData();

  return { props: { footerData, seo }, notFound };
}
